const CSRF_COOKIE_NAME = 'shareweb_csrf';
export const CSRF_HEADER_NAME = 'X-CSRF-Token';

export function getCsrfToken(): string | null {
  const cookies = document.cookie.split(';');
  for (const entry of cookies) {
    const [name, ...valueParts] = entry.trim().split('=');
    if (name === CSRF_COOKIE_NAME) {
      const value = valueParts.join('=');
      return value ? decodeURIComponent(value) : null;
    }
  }

  return null;
}

export function csrfHeaders(): Record<string, string> {
  const token = getCsrfToken();
  return token ? { [CSRF_HEADER_NAME]: token } : {};
}

/** Headers for unsafe methods — fails closed when the CSRF cookie is missing. */
export function requireCsrfHeaders(): Record<string, string> {
  const token = getCsrfToken();
  if (!token) {
    throw new Error('csrf_token_missing');
  }
  return { [CSRF_HEADER_NAME]: token };
}

/**
 * Guest bootstrap POSTs (verify / TOTP) are exempt from CSRF on the server
 * because no session cookie exists yet. Mirror that here so fail-closed CSRF
 * does not block first-time guest entry in a private window.
 */
export function isGuestAuthCsrfExemptPath(path: string): boolean {
  const pathname = path.split('?', 1)[0] ?? path;
  return pathname.endsWith('/verify')
    || pathname.endsWith('/totp/confirm')
    || pathname.endsWith('/totp/verify')
    || pathname === '/api/quick-share/claim';
}

/** Paths the API exempts from CSRF even when a leftover session cookie exists. */
export function isAuthBootstrapCsrfExemptPath(path: string): boolean {
  const pathname = path.split('?', 1)[0] ?? path;
  return pathname === '/api/auth/dev-login';
}

function isCsrfSoftExemptPath(path: string): boolean {
  return isGuestAuthCsrfExemptPath(path) || isAuthBootstrapCsrfExemptPath(path);
}

/** CSRF headers for an API request — fail-closed except guest-auth exemptions. */
export function csrfHeadersForRequest(path: string, method?: string): Record<string, string> {
  if (!isUnsafeMethod(method)) {
    return {};
  }
  if (isCsrfSoftExemptPath(path)) {
    return csrfHeaders();
  }
  return requireCsrfHeaders();
}

export function isUnsafeMethod(method: string | undefined): boolean {
  const normalized = (method ?? 'GET').toUpperCase();
  return normalized !== 'GET' && normalized !== 'HEAD' && normalized !== 'OPTIONS';
}
